import { Line } from 'react-chartjs-2';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Chart as ChartJS, TimeScale, ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-dayjs-4';

import { DataRelease } from '@/modules/calendar/model/calendar/data-release';
import { SYMBOL_SETS, SymbolSetKey } from '@/modules/calendar/model/data/data-impact-symbol-sets';

import vlinePlugin from '@/modules/calendar/components/data/impact-charting/chartjs-line-plugin';
import { isTimestampValidForChart } from '@/modules/calendar/components/data/impact-charting/chart-timestamps';
import { useChartData } from '@/modules/calendar/components/data/impact-charting/use-chart-data';
import {
  ZoomControls,
  SymbolSetSelector,
  SymbolsTooltip,
} from '@/modules/calendar/components/data/impact-charting/chart-controls';
import { payloadToChartData } from '@/modules/calendar/components/data/impact-charting/chart-transforms';

// register time scale for proper time-based x-axis
ChartJS.register(TimeScale);
ChartJS.register(vlinePlugin);

export default function DataReleaseHistoryChartAPI({
  selectedRelease,
  eventDate,
}: {
  selectedRelease: DataRelease;
  eventDate: string;
}) {
  const [data, setData] = useState<ChartData<'line'>>();
  // grid color is derived from CSS when building options
  const [chartOptions, setChartOptions] = useState<ChartOptions<'line'>>();
  const [zoomLevel, setZoomLevel] = useState<'1m' | '10m' | '1h' | 'full'>('full');
  const [symbolSet, setSymbolSet] = useState<SymbolSetKey>('sp500');
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [hoveredKey, setHoveredKey] = useState<SymbolSetKey | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();

  // Set zoom by updating chart x-axis min/max
  function setChartZoom(level: '1m' | '10m' | '1h' | 'full') {
    setZoomLevel(level);
  }

  // Apply zoom whenever data or zoom level changes
  useEffect(() => {
    if (!data || !data.labels || data.labels.length === 0) return;

    let minX: number | undefined, maxX: number | undefined;
    // Try to center on eventDate if possible
    let centerMs: number | undefined;
    if (eventDate) {
      const parsed = dayjs.tz(eventDate, 'America/New_York');
      if (parsed.isValid()) centerMs = parsed.valueOf();
    }

    // If labels are numbers, use them as ms
    const labels = data.labels as number[];
    if (zoomLevel === 'full' || !centerMs) {
      minX = Math.min(...labels);
      maxX = Math.max(...labels);
    } else {
      if (zoomLevel === '1m') {
        minX = centerMs - 60 * 1000;
        maxX = centerMs + 60 * 1000;
      } else if (zoomLevel === '10m') {
        minX = centerMs - 10 * 60 * 1000;
        maxX = centerMs + 10 * 60 * 1000;
      } else if (zoomLevel === '1h') {
        minX = centerMs - 60 * 60 * 1000;
        maxX = centerMs + 60 * 60 * 1000;
      }

      // Clamp to available data, ensure minX/maxX are numbers
      const minLabel = Math.min(...labels);
      const maxLabel = Math.max(...labels);
      minX = typeof minX === 'number' ? Math.max(minX, minLabel) : minLabel;
      maxX = typeof maxX === 'number' ? Math.min(maxX, maxLabel) : maxLabel;
    }

    setChartOptions((prev: ChartOptions<'line'> | undefined) => ({
      ...(prev || {}),
      scales: {
        ...(prev?.scales || {}),
        x: {
          ...(prev?.scales?.x || {}),
          min: minX,
          max: maxX,
        },
      },
    }));
  }, [data, zoomLevel, eventDate]);

  // Compute event timestamp and session based on selectedRelease/eventDate
  let eventTimestampMs: number | undefined;
  try {
    if (eventDate) {
      const parsed = dayjs.tz ? dayjs.tz(eventDate, 'America/New_York') : dayjs(eventDate);
      if (parsed.isValid && parsed.isValid()) eventTimestampMs = parsed.valueOf();
    }
  } catch (err: unknown) {
    console.warn('Failed to parse eventDate to ms', eventDate, err);
  }

  // Determine session (regular / extended) if we have a timestamp
  let session: 'regular' | 'extended' | null = null;
  if (eventTimestampMs) {
    const eventEst = dayjs.tz
      ? dayjs.tz(eventTimestampMs, 'America/New_York')
      : dayjs(eventTimestampMs);
    const eventHour = eventEst.hour();
    const eventMinute = eventEst.minute();
    const isBetween = (
      h: number,
      m: number,
      startH: number,
      startM: number,
      endH: number,
      endM: number,
    ) => {
      const t = h * 60 + m;
      const start = startH * 60 + startM;
      const end = endH * 60 + endM;
      return t >= start && t < end;
    };
    const isRegular = isBetween(eventHour, eventMinute, 9, 30, 16, 0);
    const isExtended = isBetween(eventHour, eventMinute, 4, 0, 20, 0);
    if (isRegular) session = 'regular';
    else if (isExtended) session = 'extended';
  }

  // Client-side timestamp recency validation
  const localValidationError =
    eventTimestampMs && !isTimestampValidForChart(eventTimestampMs)
      ? 'Chart data is not available for very recent events. Please select a date at least two days in the past.'
      : undefined;

  const {
    data: apiJson,
    loading: apiLoading,
    error: apiError,
  } = useChartData(eventTimestampMs, session, symbolSet);

  // Transform API payload into chart data when it arrives
  useEffect(() => {
    // prefer local validation error first
    if (localValidationError) {
      setError(localValidationError);
      setLoading(false);
      return;
    }

    if (!selectedRelease) return;

    setError(undefined);
    setLoading(apiLoading);

    if (apiError) {
      setError(apiError);
      setLoading(false);
      return;
    }

    if (!apiJson) return;

    const payload = apiJson.data || [];

    // Check if no symbols were returned
    if (payload.length === 0) {
      setError('No data available. This time period may have insufficient market activity.');
      setLoading(false);
      return;
    }

    try {
      const { chartData: newChartData, chartOptions: newChartOptions } = payloadToChartData(
        payload,
        eventDate,
      );
      setData(newChartData as ChartData<'line'>);
      setChartOptions(newChartOptions);
    } catch (err: unknown) {
      console.error('Failed to transform chart payload', err);
      setError('Failed to render chart data');
    } finally {
      setLoading(false);
    }
  }, [apiJson, apiLoading, apiError, eventDate, selectedRelease, symbolSet, localValidationError]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Removed dropdown, always use first series */}
      <div className={'mb-3 w-100 '} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div className="d-flex justify-content-end mb-2">
          <div
            style={{
              display: 'inline-grid',
              gridTemplateColumns: 'auto 1fr auto',
              alignItems: 'center',
              gap: 8,
              position: 'relative',
            }}
            onMouseEnter={() => setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
          >
            {/* Defensive guards: avoid rendering undefined component exports which cause React to throw */}
            {ZoomControls ? (
              <ZoomControls zoomLevel={zoomLevel} onChange={setChartZoom} />
            ) : (
              <div style={{ display: 'inline-block' }}>
                {console &&
                  (console.error
                    ? (console.error('ZoomControls component is undefined'), null)
                    : null)}
                <div className="btn-group" role="group" aria-label="zoom-controls">
                  <button
                    type="button"
                    className={`btn btn-sm btn-outline-secondary${zoomLevel === '1m' ? ' active' : ''}`}
                    onClick={() => setChartZoom('1m')}
                  >
                    1m
                  </button>
                </div>
              </div>
            )}

            {/* middle column is implicit (1fr) */}

            {/* Symbol set selector (controlled) */}
            <div style={{ position: 'relative' }}>
              <SymbolSetSelector
                symbolSet={symbolSet}
                onSelect={k => {
                  setSymbolSet(k);
                  setMenuOpen(false);
                  setTooltipVisible(false); // Hide tooltip on select
                  setHoveredKey(null); // Remove hovered key on select
                }}
                onHover={k => setHoveredKey(k)}
                onLeave={() => setHoveredKey(null)}
                menuOpen={menuOpen}
                setMenuOpen={v => setMenuOpen(v)}
              />
            </div>

            {/* Tooltip positioned relative to the entire controls block so it stretches
                from the left edge of the zoom buttons to the right edge of the dropdown */}
            <SymbolsTooltip
              text={SYMBOL_SETS[hoveredKey ?? symbolSet].desc}
              visible={Boolean(tooltipVisible || hoveredKey)}
            />
          </div>
        </div>
        <div style={{ height: '100%', position: 'relative', overflow: 'hidden', flex: 1 }}>
          {loading && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading chart data...</span>
              </div>
              <span className="ms-3">Loading chart data...</span>
            </div>
          )}
          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <div className="alert alert-warning" role="alert">
                {error}
              </div>
            </div>
          )}
          {!loading && !error && data && (
            <Line
              data={data}
              style={{ height: '100%' }}
              options={{
                ...(chartOptions || {}),
                elements: { point: { radius: 2 } },
                // ensure the chart fills the container and doesn't overflow
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
